import LoginLayout from '@/components/LoginLayout/LoginLayout';
import GuestUserStore from '@/store/guestUser';
import useDeliveryServices from '@/hooks/useDeliveryServices';

import { Shop } from '@/store/shop';
import { PostalCode } from '@/store/isAddressRegistered';
import { getShopsForDeliveryV3 } from '@/api/Shops';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext';
import { getLayoutProps } from '@/utils/layout/layout';
import { getOrFetchHeaderData } from '@/utils/headerCalls';
import { GuestAddAddress as GuestAddress, Text, Button } from '@desquared/wave-components-library';
import { useRouter } from 'next/router';
import { useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { Heading, HeadingEnum, TextEnum, BoldEnum, ArrowBack } from '@desquared/wave-components-library';
import { useTranslations } from 'next-intl';
import { useConfiguration } from '@/contexts/ConfigContext';
import { SelectedShopId } from '@/store/isAddressRegistered';
import styles from '../../components/GuestAddAddress/GuestAddAddress.module.css';

export default function GuestAddAddress({ responsiveLogo, loginConfig, loginImage, addressUnsupportedImage }) {
  const router = useRouter();
  const { showToast } = useToast();
  const { selectedService } = useDeliveryServices();
  const [formSubmissionIsLoading, setFormSubmissionsIsLoading] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [formValue, setFormValue] = useState();
  const [addressUnsupported, setAddressUnsuported] = useState(false);
  const { config } = useConfiguration();
  const t = useTranslations();

  const addressRestrictions = useMemo(() => {
    if (!config) return;

    let restrictions = {};

    if (config?.fields?.address) {
      for (const key of ['street', 'streetNo', 'postalCode']) {
        restrictions[key] = {
          maxLength: config?.fields?.address?.[key]?.maxLength,
          minLength: config?.fields?.address?.[key]?.minLength,
          required: true,
          errorMessage: {
            required: t('common.guest_address.errors.required'),
            maxLength: t('common.guest_address.erros.maxLength', { number: config?.fields?.address?.[key]?.maxLength }),
            minLength: t('common.guest_address.errors.minLength', {
              number: config?.fields?.address?.[key]?.minLength,
            }),
          },
        };
      }

      if (config?.fields?.address?.postalCode?.format === 'ALPHANUMERIC') {
        restrictions.postalCode.pattern = /^[A-Za-z0-9]+$/;
        restrictions.postalCode.errorMessage = {
          ...restrictions.postalCode.errorMessage,
          pattern: t('common.guest_address.errors.alphanumeric'),
        };
      } else {
        restrictions.postalCode.pattern = /^[0-9]+$/;
        restrictions.postalCode.errorMessage = {
          ...restrictions.postalCode.errorMessage,
          pattern: t('common.guest_address.errors.numeric'),
        };
      }
    }

    if ('postalCode' in restrictions) {
      restrictions['postal'] = { ...restrictions.postalCode };
      delete restrictions['postalCode'];
    }

    return restrictions;
  }, [config, t]);

  async function handleSubmitAddress() {
    setFormSubmissionsIsLoading(true);
    const shops = await getShopsForDeliveryV3(selectedService?.id, formValue.postal);
    Shop.setShopByDeliveryId(selectedService?.id, shops);
    Shop.setSelectedShop(shops[0]);

    SelectedShopId.update(shops[0]._id);
    if (!shops || shops?.length === 0) {
      setAddressUnsuported(true);
    } else {
      showToast(t('common.guest_address.toast.supported', { service: selectedService.title }));
      router.push('/');
    }

    PostalCode.update(formValue.postal);
    GuestUserStore.update({
      address: {
        postalCode: formValue.postal,
        street: formValue.street,
        streetNo: formValue.streetNo,
      },
    });

    setFormSubmissionsIsLoading(false);
  }

  const onValidAddressChanges = useCallback(
    (value, valid) => {
      setFormIsValid(valid);
      setFormValue(value);
    },
    [setFormIsValid, setFormValue]
  );

  return (
    <LoginLayout type={loginConfig} image={loginImage} responsiveLogo={responsiveLogo}>
      <div className={styles.loginLayoutContainer}>
        {addressUnsupported ? (
          <div className={styles.addressUnsupportedContainer}>
            <img src={addressUnsupportedImage} alt={t('common.guest_address.unsuported.title')}></img>
            <div className={styles.addressUnsupportedText}>
              <Heading bold={BoldEnum.Bold} type={HeadingEnum.HeadingL}>
                {t('common.guest_address.unsuported.title')}
              </Heading>
              <Text type={TextEnum.Body}>{t('common.guest_address.unsuported.description')}</Text>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.loginLayoutHeader}>
              <ArrowBack color="black" onClick={router.back} />
              <Heading type={HeadingEnum.HeadingM} bold={BoldEnum.Bold}>
                {t('common.guest_address.header')}
              </Heading>
            </div>
            <GuestAddress
              autocompleteConfig={{ restrictAutoCompleteToCountry: process.env.defaultAutocompleteCountryCode }}
              onValidAddressChange={onValidAddressChanges}
              map={{
                apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
                center: {
                  lat: process.env.defaultMapCenter.lat,
                  lon: process.env.defaultMapCenter.lng,
                },
                locale: router.locale,
                stigmas: [],
                logo: responsiveLogo,
              }}
              wordings={{
                footerBtn: t('common.guest_address.header'),
                formPlaceholders: {
                  address: t('common.guest_address.form_fields.address'),
                  street: t('common.guest_address.form_fields.street'),
                  streetNo: t('common.guest_address.form_fields.street_no'),
                  postal: t('common.guest_address.form_fields.postal'),
                },
              }}
              restrictions={addressRestrictions}
            />
          </>
        )}
        <footer className={styles.loginLayoutFooter}>
          {addressUnsupported ? (
            <div className={styles.loginLayoutFooterUnsupported}>
              <Button
                variant="text"
                onClick={() => setAddressUnsuported(false)}
                text={t('common.guest_address.unsuported.new_address_btn')}
                className={styles.focusClass}
              />
              <Button
                variant="filled"
                onClick={() => router.push('select-service')}
                text={t('common.guest_address.unsuported.change_service_btn')}
                className={styles.focusClass}
              />
            </div>
          ) : (
            <Button
              onClick={handleSubmitAddress}
              text={t('common.guest_address.header')}
              variant={'filled'}
              disabled={!formIsValid || formSubmissionIsLoading}
              className={styles.focusClass}
            ></Button>
          )}
        </footer>
      </div>
    </LoginLayout>
  );
}

export async function getStaticProps(context) {
  const { responsiveLogo, emptyStates } = await getLayoutProps({ headerProps: { selectedIndex: 0 } }, context.locale);

  const [{ data: loginAllData }] = await Promise.all([getOrFetchHeaderData('login_page', context.locale)]);
  const loginConfig = loginAllData.data[0].content.configuration;
  const loginImage = loginAllData.data[0].content.image?.url ? loginAllData.data[0].content.image?.url : '';

  const messages = {
    common: (await import(`../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    login_register: (await import(`../../../prebuild-data/i18n/${context.locale}/login_register.json`)).default,
  };

  const addressUnsupportedImage = emptyStates?.address_empty_state?.url ? emptyStates?.address_empty_state?.url : null;

  return {
    props: {
      responsiveLogo,
      loginConfig,
      loginImage,
      addressUnsupportedImage,
      messages,
    },
  };
}
